import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Minus, Plus, ChevronLeft } from "lucide-react";
import { cldImage } from "@/lib/cldImage";

const inputCls =
  "w-full bg-transparent border-b border-line py-3 focus:outline-none focus:border-gold placeholder:text-espresso-light/70 text-espresso";

const OCCASIONS = [
  { key: "corporate", label: "Corporate / Office" },
  { key: "birthday",  label: "Birthday" },
  { key: "wedding",   label: "Wedding / Engagement" },
  { key: "hosting",   label: "Hosting at home" },
  { key: "gifting",   label: "Gifting" },
  { key: "other",     label: "Something else" },
];

const FLAVORS = ["Traditional Italian", "Biscoff", "Pistachio", "Nutella"];

const STEPS = [
  "Occasion",
  "Quantity",
  "Flavours",
  "When",
  "Delivery",
  "Contact",
  "Review",
];

function todayISO() {
  const d = new Date();
  d.setDate(d.getDate() + 2); // bulk needs at least ~48h heads-up
  return d.toISOString().slice(0, 10);
}

export default function Bulk() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [occasion, setOccasion] = useState("");
  const [occasionOther, setOccasionOther] = useState("");
  const [quantity, setQuantity] = useState(20);
  const [selections, setSelections] = useState({});
  const [curated, setCurated] = useState(false);
  const [date, setDate] = useState(todayISO());
  const [time, setTime] = useState("");
  const [fulfillment, setFulfillment] = useState("delivery");
  const [customer, setCustomer] = useState({
    full_name: "", mobile: "", email: "",
    address_line1: "", address_line2: "", area: "", city: "Ghaziabad",
    pincode: "", landmark: "", instructions: "",
  });
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);

  const totalSelected = useMemo(
    () => Object.values(selections).reduce((a, b) => a + b, 0),
    [selections]
  );
  const remaining = quantity - totalSelected;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [step]);

  const bumpFlavor = (f, d) => {
    if (curated) return;
    if (d > 0 && remaining <= 0) return;
    setSelections((s) => {
      const next = { ...s, [f]: Math.max(0, (s[f] || 0) + d) };
      if (next[f] === 0) delete next[f];
      return next;
    });
  };

  const applyCurated = () => {
    setCurated(true);
    // Even split across 4 signature flavours
    const per = Math.floor(quantity / 4);
    const rem = quantity - per * 4;
    const c = { "Traditional Italian": per, "Biscoff": per, "Pistachio": per, "Nutella": per };
    if (rem > 0) c["Traditional Italian"] += rem;
    setSelections(c);
  };

  const clearCurated = () => {
    setCurated(false);
    setSelections({});
  };

  const canProceed = () => {
    switch (step) {
      case 0: return !!occasion && (occasion !== "other" || occasionOther.trim().length > 1);
      case 1: return quantity >= 10 && quantity <= 500;
      case 2: return totalSelected === quantity;
      case 3: return !!date;
      case 4: return fulfillment === "pickup" || (customer.address_line1 && customer.pincode);
      case 5: return customer.full_name && customer.mobile && customer.email;
      case 6: return true;
      default: return false;
    }
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        occasion,
        occasion_other: occasion === "other" ? occasionOther.trim() : null,
        quantity,
        selections: Object.entries(selections).map(([flavor, qty]) => ({ flavor, qty })),
        curated,
        date,
        time: time || null,
        fulfillment,
        customer,
        notes: notes.trim() || null,
      };
      const { data } = await api.post("/bulk-orders", payload);
      setSubmitted(data);
      window.scrollTo({ top: 0 });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <main className="max-w-[900px] mx-auto px-6 md:px-10 py-16 md:py-28" data-testid="bulk-success">
        <div className="border-b border-line pb-10">
          <div className="label-xs text-gold">Bulk request received</div>
          <h1 className="mt-4 font-serif text-5xl md:text-6xl leading-[0.95] text-espresso">
            Thank you.<br/><span className="italic text-espresso-light">We'll be in touch.</span>
          </h1>
        </div>
        <p className="mt-10 text-espresso-light max-w-xl leading-relaxed">
          Your request <span className="text-espresso font-medium">{submitted.reference}</span> is with our team.
          We personally confirm every bulk order — expect a call or WhatsApp within a few hours
          (usually within the same day, Mon–Sat 10:00–19:00).
        </p>
        <div className="mt-10 grid md:grid-cols-2 gap-6 border-t border-line pt-10">
          <div>
            <div className="label-xs text-espresso-light">Occasion</div>
            <div className="font-serif text-2xl text-espresso mt-2">
              {OCCASIONS.find((o) => o.key === submitted.occasion)?.label}
              {submitted.occasion_other && ` — ${submitted.occasion_other}`}
            </div>
          </div>
          <div>
            <div className="label-xs text-espresso-light">Quantity</div>
            <div className="font-serif text-2xl text-espresso mt-2">{submitted.quantity} cups</div>
          </div>
          <div>
            <div className="label-xs text-espresso-light">When</div>
            <div className="font-serif text-xl text-espresso mt-2">{submitted.date}{submitted.time ? ` · ${submitted.time}` : ""}</div>
          </div>
          <div>
            <div className="label-xs text-espresso-light">Fulfilment</div>
            <div className="font-serif text-xl text-espresso mt-2 capitalize">{submitted.fulfillment}</div>
          </div>
        </div>
        <div className="mt-12">
          <Link to="/" className="btn-espresso" data-testid="bulk-home-btn">Back to home</Link>
        </div>
      </main>
    );
  }

  return (
    <main data-testid="bulk-page" className="max-w-[1000px] mx-auto px-6 md:px-10 py-10 md:py-16">
      <Link to="/" className="label-xs text-espresso-light link-underline" data-testid="bulk-back-home">
        ← Home
      </Link>
      <div className="mt-6 grid md:grid-cols-12 gap-10 items-end border-b border-line pb-8">
        <div className="md:col-span-8">
          <div className="label-xs text-espresso-light">Bulk orders · 10 cups and above</div>
          <h1 className="mt-4 font-serif text-5xl md:text-6xl leading-[0.95] text-espresso">
            From office treats<br/>
            <span className="italic text-espresso-light">to celebrations.</span>
          </h1>
        </div>
        <div className="md:col-span-4 text-right">
          <div className="label-xs text-espresso-light">Step {step + 1} of {STEPS.length}</div>
          <div className="mt-2 font-serif text-xl text-espresso">{STEPS[step]}</div>
        </div>
      </div>

      {/* Corporate landing story — warms the pitch, doesn't shout */}
      {step === 0 && (
        <section className="mt-14 mb-4" data-testid="bulk-story">
          <div className="grid md:grid-cols-12 gap-8 items-start">
            <div className="md:col-span-3 aspect-[3/4] overflow-hidden bg-ivory-100">
              <img
                src={cldImage("https://images.unsplash.com/photo-1519671482749-fd09be7ccebf", { w: 900 })}
                alt="An office table set with dessert cups"
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
            <div className="md:col-span-6 md:pt-6">
              <div className="label-xs text-espresso-light">Handled with care</div>
              <p className="mt-3 font-serif text-2xl md:text-3xl text-espresso leading-snug">
                Every bulk order is layered by hand,<br/>
                <span className="italic text-espresso-light">the day it reaches you.</span>
              </p>
              <p className="mt-5 text-espresso-light leading-relaxed">
                Boardroom milestones, wedding-week desserts, hosting fifty at home — we treat each request
                as if it were four. Small-batch mascarpone, single-origin espresso, boutique packaging,
                and a printed note if you'd like one.
              </p>
              <ul className="mt-6 space-y-2 text-sm text-espresso-light">
                <li className="flex gap-3"><span className="text-gold">·</span>Minimum 10 cups. No maximum in principle — 48 hours notice preferred.</li>
                <li className="flex gap-3"><span className="text-gold">·</span>Delivered chilled in kraft carriers with wooden spoons and cocoa sachets.</li>
                <li className="flex gap-3"><span className="text-gold">·</span>Corporate branding, gift tags and custom sleeves on request.</li>
              </ul>
            </div>
            <div className="md:col-span-3 aspect-[3/4] overflow-hidden bg-ivory-100 md:mt-16">
              <img
                src={cldImage("https://images.unsplash.com/photo-1519741497674-611481863552", { w: 900 })}
                alt="A wedding celebration table"
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
          </div>
          <div className="mt-10 border-t border-line pt-6 flex flex-wrap items-baseline gap-x-8 gap-y-3">
            <div className="label-xs text-espresso-light">Recently made for</div>
            <div className="font-serif italic text-espresso-light">Product launches · 25th anniversaries · Sangeet evenings · Diwali gifting · Team offsites</div>
          </div>
        </section>
      )}


      {/* Progress bar */}
      <div className="mt-8 flex gap-1" data-testid="bulk-progress">
        {STEPS.map((_, i) => (
          <div key={i} className={`h-[2px] flex-1 ${i <= step ? "bg-espresso" : "bg-line"}`} />
        ))}
      </div>

      <div className="mt-12 min-h-[400px]">
        {step === 0 && (
          <section data-testid="step-occasion">
            <div className="label-xs text-espresso-light">01 · What's it for?</div>
            <h2 className="mt-3 font-serif text-3xl md:text-4xl text-espresso leading-tight">Tell us the occasion.</h2>
            <p className="mt-3 text-espresso-light">A quick line helps us think ahead — packaging, timing, and any little touches.</p>
            <div className="mt-8 grid md:grid-cols-2 gap-3">
              {OCCASIONS.map((o) => (
                <button
                  key={o.key}
                  onClick={() => setOccasion(o.key)}
                  data-testid={`occasion-${o.key}`}
                  className={`text-left border p-6 transition-colors ${occasion === o.key ? "border-espresso bg-espresso text-ivory" : "border-line text-espresso hover:border-espresso"}`}
                >
                  <div className="font-serif text-xl">{o.label}</div>
                </button>
              ))}
            </div>
            {occasion === "other" && (
              <input
                autoFocus
                placeholder="Tell us a little more…"
                className={`${inputCls} mt-8`}
                value={occasionOther}
                onChange={(e) => setOccasionOther(e.target.value)}
                data-testid="occasion-other-input"
              />
            )}
          </section>
        )}

        {step === 1 && (
          <section data-testid="step-quantity">
            <div className="label-xs text-espresso-light">02 · How many cups?</div>
            <h2 className="mt-3 font-serif text-3xl md:text-4xl text-espresso leading-tight">Roughly how many?</h2>
            <p className="mt-3 text-espresso-light">Minimum 10. We'll confirm exact pricing once we hear back.</p>
            <div className="mt-10 flex items-center gap-8">
              <button onClick={() => setQuantity(Math.max(10, quantity - 5))} data-testid="qty-minus"
                className="w-14 h-14 rounded-full border border-line flex items-center justify-center hover:border-espresso transition-colors">
                <Minus size={20} strokeWidth={1.5} />
              </button>
              <input
                type="number"
                min={10}
                max={500}
                value={quantity}
                onChange={(e) => setQuantity(Math.max(10, Math.min(500, parseInt(e.target.value) || 10)))}
                className="font-serif text-6xl bg-transparent w-40 text-center border-b border-line focus:outline-none focus:border-gold text-espresso"
                data-testid="qty-input"
              />
              <button onClick={() => setQuantity(Math.min(500, quantity + 5))} data-testid="qty-plus"
                className="w-14 h-14 rounded-full border border-line flex items-center justify-center hover:border-espresso transition-colors">
                <Plus size={20} strokeWidth={1.5} />
              </button>
              <div className="text-espresso-light">
                <div className="label-xs">Cups</div>
                <div className="font-serif italic text-espresso mt-1">enough for a room.</div>
              </div>
            </div>
            <div className="mt-10 flex flex-wrap gap-3">
              {[15, 25, 50, 75, 100].map((n) => (
                <button
                  key={n}
                  onClick={() => setQuantity(n)}
                  data-testid={`qty-preset-${n}`}
                  className={`label-xs px-4 py-2 rounded-full border transition-colors ${quantity === n ? "bg-espresso text-ivory border-espresso" : "border-line text-espresso hover:border-espresso"}`}
                >
                  {n} cups
                </button>
              ))}
            </div>
          </section>
        )}

        {step === 2 && (
          <section data-testid="step-flavours">
            <div className="label-xs text-espresso-light">03 · Flavours</div>
            <h2 className="mt-3 font-serif text-3xl md:text-4xl text-espresso leading-tight">Mix your {quantity} cups.</h2>
            <p className="mt-3 text-espresso-light">Choose your split — or let us curate a signature mix.</p>

            <div className="mt-6 flex items-baseline justify-between border-b border-line pb-4">
              <div>
                {!curated ? (
                  <button onClick={applyCurated} className="label-xs link-underline text-espresso" data-testid="curated-apply">
                    Let us choose for you →
                  </button>
                ) : (
                  <div className="flex items-center gap-4">
                    <span className="font-serif italic text-espresso-light">Curated selection applied.</span>
                    <button onClick={clearCurated} className="label-xs link-underline text-espresso" data-testid="curated-clear">
                      Choose myself →
                    </button>
                  </div>
                )}
              </div>
              <div className={`font-serif text-lg ${totalSelected === quantity ? "text-gold-hover" : "text-espresso-light"}`}>
                {totalSelected} / {quantity} {totalSelected === quantity && "✓"}
              </div>
            </div>

            <div className="mt-4 space-y-2">
              {FLAVORS.map((f) => {
                const qty = selections[f] || 0;
                const active = qty > 0;
                return (
                  <div key={f} data-testid={`bulk-flavor-${f}`}
                    className={`flex items-center justify-between px-5 py-4 border transition-colors ${active ? "border-espresso bg-ivory-100" : "border-line"} ${curated ? "opacity-70" : ""}`}>
                    <div>
                      <div className="font-serif text-xl text-espresso">{f}</div>
                      {active && <div className="label-xs text-gold-hover mt-1">×{qty}</div>}
                    </div>
                    <div className="flex items-center gap-3">
                      <button onClick={() => bumpFlavor(f, -1)} disabled={!qty || curated}
                        data-testid={`bulk-flavor-dec-${f}`}
                        className="w-9 h-9 rounded-full border border-line flex items-center justify-center disabled:opacity-30 hover:border-espresso transition-colors">
                        <Minus size={16} strokeWidth={1.5} />
                      </button>
                      <span className="w-8 text-center font-serif text-lg">{qty}</span>
                      <button onClick={() => bumpFlavor(f, +1)} disabled={remaining <= 0 || curated}
                        data-testid={`bulk-flavor-inc-${f}`}
                        className="w-9 h-9 rounded-full border border-line flex items-center justify-center disabled:opacity-30 hover:border-espresso transition-colors">
                        <Plus size={16} strokeWidth={1.5} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {step === 3 && (
          <section data-testid="step-when">
            <div className="label-xs text-espresso-light">04 · When?</div>
            <h2 className="mt-3 font-serif text-3xl md:text-4xl text-espresso leading-tight">Date and time.</h2>
            <p className="mt-3 text-espresso-light">Please allow at least 48 hours — the layering is slow, and worth waiting for.</p>
            <div className="mt-8 grid md:grid-cols-2 gap-6 max-w-lg">
              <div>
                <label className="label-xs text-espresso-light">Date</label>
                <input type="date" min={todayISO()} value={date} onChange={(e) => setDate(e.target.value)} className={inputCls} data-testid="bulk-date" />
              </div>
              <div>
                <label className="label-xs text-espresso-light">Preferred time (optional)</label>
                <input type="time" value={time} onChange={(e) => setTime(e.target.value)} className={inputCls} data-testid="bulk-time" placeholder="HH:MM" />
              </div>
            </div>
          </section>
        )}

        {step === 4 && (
          <section data-testid="step-fulfillment">
            <div className="label-xs text-espresso-light">05 · Delivery or pickup?</div>
            <h2 className="mt-3 font-serif text-3xl md:text-4xl text-espresso leading-tight">How should it reach you?</h2>
            <div className="mt-8 grid md:grid-cols-2 gap-3 max-w-2xl">
              {["delivery", "pickup"].map((f) => (
                <button key={f} onClick={() => setFulfillment(f)} data-testid={`bulk-fulfillment-${f}`}
                  className={`text-left border p-6 transition-colors ${fulfillment === f ? "border-espresso bg-espresso text-ivory" : "border-line text-espresso hover:border-espresso"}`}>
                  <div className="label-xs">{f}</div>
                  <div className="font-serif text-xl mt-2">{f === "delivery" ? "To your venue" : "From our kitchen"}</div>
                </button>
              ))}
            </div>

            {fulfillment === "delivery" && (
              <div className="mt-8 grid md:grid-cols-2 gap-6">
                <input placeholder="Address line 1" className={inputCls} value={customer.address_line1} onChange={(e) => setCustomer({ ...customer, address_line1: e.target.value })} data-testid="bulk-addr1" />
                <input placeholder="Address line 2" className={inputCls} value={customer.address_line2} onChange={(e) => setCustomer({ ...customer, address_line2: e.target.value })} data-testid="bulk-addr2" />
                <input placeholder="Area / locality" className={inputCls} value={customer.area} onChange={(e) => setCustomer({ ...customer, area: e.target.value })} data-testid="bulk-area" />
                <input placeholder="City" className={inputCls} value={customer.city} onChange={(e) => setCustomer({ ...customer, city: e.target.value })} data-testid="bulk-city" />
                <input placeholder="Pincode" className={inputCls} value={customer.pincode} onChange={(e) => setCustomer({ ...customer, pincode: e.target.value })} data-testid="bulk-pincode" />
                <input placeholder="Landmark (optional)" className={inputCls} value={customer.landmark} onChange={(e) => setCustomer({ ...customer, landmark: e.target.value })} data-testid="bulk-landmark" />
              </div>
            )}
          </section>
        )}

        {step === 5 && (
          <section data-testid="step-contact">
            <div className="label-xs text-espresso-light">06 · You</div>
            <h2 className="mt-3 font-serif text-3xl md:text-4xl text-espresso leading-tight">Who do we speak to?</h2>
            <div className="mt-8 grid md:grid-cols-2 gap-6 max-w-2xl">
              <input placeholder="Full name" className={inputCls} value={customer.full_name} onChange={(e) => setCustomer({ ...customer, full_name: e.target.value })} data-testid="bulk-name" />
              <input placeholder="Mobile" className={inputCls} value={customer.mobile} onChange={(e) => setCustomer({ ...customer, mobile: e.target.value })} data-testid="bulk-mobile" />
              <input placeholder="Email" type="email" className={`${inputCls} md:col-span-2`} value={customer.email} onChange={(e) => setCustomer({ ...customer, email: e.target.value })} data-testid="bulk-email" />
              <textarea rows="3" placeholder="Anything else? Branding, timing, dietary notes." className={`${inputCls} md:col-span-2`} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="bulk-notes" />
            </div>
          </section>
        )}

        {step === 6 && (
          <section data-testid="step-review">
            <div className="label-xs text-espresso-light">07 · Review</div>
            <h2 className="mt-3 font-serif text-3xl md:text-4xl text-espresso leading-tight">Ready to send?</h2>
            <p className="mt-3 text-espresso-light">We'll come back with pricing and confirm within a few hours.</p>

            <div className="mt-10 border border-line divide-y divide-line">
              <SummaryRow label="Occasion" value={`${OCCASIONS.find((o) => o.key === occasion)?.label}${occasion === "other" ? ` — ${occasionOther}` : ""}`} onEdit={() => setStep(0)} />
              <SummaryRow label="Quantity" value={`${quantity} cups`} onEdit={() => setStep(1)} />
              <SummaryRow
                label="Flavours"
                value={
                  <ul className="space-y-1">
                    {Object.entries(selections).map(([f, n]) => (
                      <li key={f} className="text-sm"><span className="text-gold mr-1">·</span>{f} × {n}</li>
                    ))}
                    {curated && <li className="text-xs italic text-espresso-light">Curated selection</li>}
                  </ul>
                }
                onEdit={() => setStep(2)}
              />
              <SummaryRow label="When" value={`${date}${time ? ` · ${time}` : ""}`} onEdit={() => setStep(3)} />
              <SummaryRow
                label="Fulfilment"
                value={fulfillment === "delivery"
                  ? `Delivery — ${customer.address_line1}, ${customer.area || ""} ${customer.pincode}`
                  : "Pickup from our kitchen"}
                onEdit={() => setStep(4)}
              />
              <SummaryRow label="Contact" value={`${customer.full_name} · ${customer.mobile} · ${customer.email}`} onEdit={() => setStep(5)} />
              {notes && <SummaryRow label="Notes" value={notes} onEdit={() => setStep(5)} />}
            </div>
          </section>
        )}
      </div>

      <div className="mt-12 flex items-center justify-between border-t border-line pt-8">
        <button
          onClick={() => (step === 0 ? navigate("/") : setStep(step - 1))}
          data-testid="bulk-back"
          className="label-xs text-espresso-light hover:text-espresso flex items-center gap-2"
        >
          <ChevronLeft size={14} strokeWidth={1.5} />
          {step === 0 ? "Cancel" : "Back"}
        </button>
        {step < STEPS.length - 1 ? (
          <button
            onClick={() => setStep(step + 1)}
            disabled={!canProceed()}
            data-testid="bulk-next"
            className="btn-espresso disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Continue
          </button>
        ) : (
          <button
            onClick={submit}
            disabled={submitting}
            data-testid="bulk-submit"
            className="btn-espresso disabled:opacity-40"
          >
            {submitting ? "Sending…" : "Send request"}
          </button>
        )}
      </div>
    </main>
  );
}

function SummaryRow({ label, value, onEdit }) {
  return (
    <div className="grid grid-cols-[120px_1fr_auto] gap-6 px-6 py-5 items-start">
      <div className="label-xs text-espresso-light pt-1">{label}</div>
      <div className="text-espresso font-serif text-lg leading-snug">{value || "—"}</div>
      <button onClick={onEdit} className="label-xs link-underline text-espresso">Edit</button>
    </div>
  );
}
